import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Copy, Play, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { mh } from "@/lib/mh";
import { errText } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { Panel, Eyebrow, DeptTag, timeAgo } from "@/components/mh/bits";
import { useDeptMap } from "@/components/mh/hooks";
import { Btn } from "@/components/mh/controls";

const IconBtn = ({ onClick, children, testId, label, disabled }) => (
  <button onClick={onClick} disabled={disabled} aria-label={label} title={label} data-testid={testId}
    className="w-9 h-9 grid place-items-center rounded-full border border-white/10 hover:border-white/30 hover:bg-white/5 transition-colors disabled:opacity-30">{children}</button>
);

export default function Workflows() {
  const { canManage } = useAuth();
  const deps = useDeptMap();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const load = () => mh.workflows().then((r) => { setItems(r.data); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); mh.templates().then((r) => setTemplates(r.data)).catch(() => {}); }, []);
  const act = (fn, msg) => async () => { try { await fn(); if (msg) toast.success(msg); load(); } catch (e) { toast.error(errText(e)); } };
  const blank = act(async () => { const r = await mh.createWorkflow({ name: "Untitled workflow", enabled: false, nodes: [], edges: [] }); nav(`/workflows/${r.data.id}`); });
  const applyTemplate = (d) => act(async () => { const r = await mh.fromTemplate(d); nav(`/workflows/${r.data.id}`); });

  return (
    <div className="max-w-[1400px] mx-auto space-y-8" data-testid="workflows-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mh-rise">
        <div>
          <Eyebrow>Workflow builder</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">Chain the house <em className="text-muted-foreground">into loops.</em></h1>
        </div>
        {canManage && <Btn variant="primary" onClick={blank} testId="new-workflow-btn"><Plus className="w-4 h-4" /> New workflow</Btn>}
      </header>

      <section className="space-y-3">
        <Eyebrow>Your workflows · {loading ? "…" : items.length}</Eyebrow>
        {loading && <p className="text-sm text-muted-foreground font-mono" data-testid="workflows-loading">Loading workflows…</p>}
        {!loading && items.length === 0 && <p className="text-sm text-muted-foreground" data-testid="workflows-empty">No workflows yet. Start from a department template below.</p>}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {items.map((w) => {
            const stepCount = (w.nodes || []).length;
            const canRun = w.enabled && stepCount > 0;
            return (
            <Panel key={w.id} cls="p-4 flex items-center gap-4" testId={`workflow-card-${w.id}`}>
              <div className="flex-1 min-w-0">
                <Link to={`/workflows/${w.id}`} className="font-medium hover:underline underline-offset-4 truncate block" data-testid={`workflow-open-${w.id}`}>{w.name}</Link>
                <div className="flex items-center gap-3 mt-1 font-mono text-[10px] text-muted-foreground">
                  {w.department && deps[w.department] && <DeptTag name={deps[w.department].name} hex={deps[w.department].hex} />}
                  <span>{stepCount} steps</span>
                  {stepCount === 0 && <span className="text-[#FFB020]" data-testid={`workflow-draft-${w.id}`}>DRAFT · ADD STEPS</span>}
                  <span>last run {timeAgo(w.last_run_at)}</span>
                </div>
              </div>
              <Switch checked={w.enabled} disabled={!canManage || stepCount === 0} data-testid={`workflow-toggle-${w.id}`}
                onCheckedChange={(v) => {
                  if (v && stepCount === 0) { toast.error("Add at least one step before switching on"); return; }
                  act(() => mh.updateWorkflow(w.id, { ...w, enabled: v }), v ? "Workflow on" : "Workflow off")();
                }} />
              {canManage && <>
                <IconBtn label={canRun ? "Run" : stepCount === 0 ? "Add steps first" : "Turn on first"} testId={`workflow-run-${w.id}`} disabled={!canRun} onClick={act(() => mh.runWorkflow(w.id), "Run started. Watch it on the canvas")}><Play className="w-4 h-4" /></IconBtn>
                <IconBtn label="Duplicate" testId={`workflow-dup-${w.id}`} onClick={act(() => mh.duplicateWorkflow(w.id), "Duplicated")}><Copy className="w-4 h-4" /></IconBtn>
                <IconBtn label="Delete" testId={`workflow-del-${w.id}`} onClick={act(() => mh.deleteWorkflow(w.id), "Deleted")}><Trash2 className="w-4 h-4" /></IconBtn>
              </>}
            </Panel>
            );
          })}
        </div>
      </section>

      <section className="space-y-3">
        <Eyebrow>Starter templates · one per department</Eyebrow>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {templates.map((t) => {
            const d = deps[t.department];
            return (
              <Panel key={t.department} cls="p-4 flex flex-col" testId={`template-${t.department}`}>
                {d && <DeptTag name={d.name} hex={d.hex} />}
                <div className="font-display text-xl mt-2">{t.name}</div>
                <ol className="mt-2 space-y-1 text-xs text-muted-foreground flex-1">{t.nodes.map((n, i) => <li key={n.id}>{i + 1}. {n.label}</li>)}</ol>
                {canManage && <div className="mt-3"><Btn onClick={applyTemplate(t.department)} testId={`use-template-${t.department}`}>Use template</Btn></div>}
              </Panel>
            );
          })}
        </div>
      </section>
    </div>
  );
}
